/**
 * Auth Token Service
 * Manages JWT access tokens for custom authentication
 *
 * Token Storage:
 * - accessToken: localStorage + memory cache
 * - refreshToken: httpOnly cookie (managed by backend, XSS-proof)
 * - auth-token cookie: httpOnly cookie (managed by backend for image auth)
 *
 * Note: All cookies are set by the backend with httpOnly flag.
 * Frontend only manages localStorage and memory cache for access tokens.
 */

// Token storage keys
const ACCESS_TOKEN_KEY = 'access_token';
const TOKEN_EXPIRES_AT_KEY = 'token_expires_at';

// In-memory cache for faster access (avoids localStorage reads)
let cachedAccessToken: string | null = null;
let cachedExpiresAt: number | null = null;

/**
 * Store access token after successful login
 * Note: All cookies (refresh-token, auth-token) are set by backend as httpOnly
 */
export function setTokens(accessToken: string, expiresIn: number): void {
  const expiresAt = Date.now() + expiresIn * 1000;

  // Store in localStorage (access token only)
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(TOKEN_EXPIRES_AT_KEY, expiresAt.toString());

  // Update memory cache
  cachedAccessToken = accessToken;
  cachedExpiresAt = expiresAt;
}

/**
 * Update access token after refresh
 * Note: Backend updates httpOnly cookies automatically
 */
export function updateAccessToken(accessToken: string, expiresIn: number): void {
  const expiresAt = Date.now() + expiresIn * 1000;

  // Store in localStorage
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(TOKEN_EXPIRES_AT_KEY, expiresAt.toString());

  // Update memory cache
  cachedAccessToken = accessToken;
  cachedExpiresAt = expiresAt;
}

/**
 * Get current access token (from cache or localStorage)
 * Returns null if no token or token is expired
 */
export function getAccessToken(): string | null {
  // Try memory cache first (faster)
  if (cachedAccessToken && cachedExpiresAt) {
    // Check if expired (with 10s buffer for API calls)
    if (cachedExpiresAt > Date.now() + 10000) {
      return cachedAccessToken;
    }
  }

  // Fallback to localStorage
  const token = localStorage.getItem(ACCESS_TOKEN_KEY);
  const expiresAtStr = localStorage.getItem(TOKEN_EXPIRES_AT_KEY);

  if (!token || !expiresAtStr) {
    return null;
  }

  const expiresAt = parseInt(expiresAtStr, 10);

  // Check if expired (with 10s buffer)
  if (expiresAt <= Date.now() + 10000) {
    return null;
  }

  // Update cache
  cachedAccessToken = token;
  cachedExpiresAt = expiresAt;

  return token;
}

/**
 * Get token expiry time (milliseconds since epoch)
 */
export function getTokenExpiresAt(): number | null {
  if (cachedExpiresAt) {
    return cachedExpiresAt;
  }

  const expiresAtStr = localStorage.getItem(TOKEN_EXPIRES_AT_KEY);
  if (!expiresAtStr) {
    return null;
  }

  return parseInt(expiresAtStr, 10);
}

/**
 * Check if token is expired or about to expire (within buffer)
 */
export function isTokenExpired(bufferMs: number = 10000): boolean {
  const expiresAt = getTokenExpiresAt();
  if (!expiresAt) {
    return true;
  }
  return expiresAt <= Date.now() + bufferMs;
}

/**
 * Check if user has stored access token (may be expired)
 * Note: Refresh token is in httpOnly cookie, not localStorage
 */
export function hasStoredAccessToken(): boolean {
  return localStorage.getItem(ACCESS_TOKEN_KEY) !== null;
}

/**
 * Clear stored access token (logout)
 * Note: httpOnly cookies (refresh-token, auth-token) are cleared by backend
 */
export function clearTokens(): void {
  // Clear localStorage
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXPIRES_AT_KEY);

  // Clear memory cache
  cachedAccessToken = null;
  cachedExpiresAt = null;
}

/**
 * Initialize token cache from localStorage on app start
 * Call this early in app initialization
 */
export function initializeTokenCache(): void {
  const token = localStorage.getItem(ACCESS_TOKEN_KEY);
  const expiresAtStr = localStorage.getItem(TOKEN_EXPIRES_AT_KEY);

  if (token && expiresAtStr) {
    cachedAccessToken = token;
    cachedExpiresAt = parseInt(expiresAtStr, 10);
  }
}
